/* eslint-disable i18next/no-literal-string */
import { useCurrentLanguage } from '@aikoa/i18n/react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ScrollView, View } from 'react-native'
import {
  EMAIL_TEMPLATES,
  SMS_TEMPLATES,
  type TemplateDefinition,
} from '~/components/admin/template-previews/templateRegistry'
import ContentBox from '~/components/layout/ContentBox'
import ScreenView from '~/components/layout/ScreenView'
import { ViewTitle } from '~/components/layout/Sections'
import { LoadingView } from '~/components/LoadingView'
import { MessagePreviewPanel } from '~/components/messages/MessagePreviewPanel'
import { Body, Label } from '~/components/typography/Content'
import { Heading3 } from '~/components/typography/Titles'
import { Button, ButtonLabel } from '~/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/components/ui/dialog'
import { Input } from '~/components/ui/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'
import { trpc } from '~/lib/trpc'

type TemplateType = 'email' | 'sms'
type AdminEmailTemplateId =
  | 'feedbackRequest'
  | 'feedbackReceived'
  | 'realtorLinkFeedbackReceived'
  | 'contactRequestResponse'
  | 'leadPurchased'
  | 'leadOutcome'
  | 'inboundIngest'
  | 'inboundError'
  | 'welcome'
  | 'realtorActivation'
type AdminSmsTemplateId = 'feedbackRequest' | 'contactRequest'

function SendTestEmailDialog({ template }: { template: TemplateDefinition }) {
  const [open, setOpen] = useState(false)
  const [recipientEmail, setRecipientEmail] = useState('')
  const [selectedVariant, setSelectedVariant] = useState(
    template.defaultVariant ?? template.variants?.[0]?.id
  )

  const sendMutation = useMutation(trpc.admin.sendTestEmail.mutationOptions())

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      sendMutation.reset()
      setRecipientEmail('')
    }
  }

  const handleSend = () => {
    if (!recipientEmail.trim()) {
      return
    }

    sendMutation.mutate(
      {
        templateId: template.id as Parameters<typeof sendMutation.mutate>[0]['templateId'],
        variant: selectedVariant,
        recipientEmail: recipientEmail.trim(),
      },
      {
        onSuccess: () => {
          setOpen(false)
        },
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <ButtonLabel>Lähetä testiviesti</ButtonLabel>
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Lähetä testiviesti</DialogTitle>
        </DialogHeader>

        <Body className="text-secondary">{template.name}</Body>

        <View className="gap-4">
          {template.variants && template.variants.length > 0 && (
            <View className="gap-2">
              <Label className="text-secondary">Variantti</Label>
              <View className="flex-row flex-wrap gap-2">
                {template.variants.map((variant) => (
                  <Button
                    key={variant.id}
                    variant={selectedVariant === variant.id ? 'default' : 'outline'}
                    size="sm"
                    onPress={() => setSelectedVariant(variant.id)}>
                    <ButtonLabel>{variant.label}</ButtonLabel>
                  </Button>
                ))}
              </View>
            </View>
          )}

          <View className="gap-2">
            <Label className="text-secondary">Vastaanottajan sähköposti</Label>
            <Input
              value={recipientEmail}
              onChangeText={setRecipientEmail}
              placeholder="esimerkki@example.com"
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
            />
          </View>

          {sendMutation.isError && (
            <Body className="text-sm text-red-500">
              Lähetys epäonnistui. Tarkista sähköpostiosoite ja yritä uudelleen.
            </Body>
          )}

          {sendMutation.isSuccess && (
            <Body className="text-sm text-green-600">Testiviesti lähetetty!</Body>
          )}
        </View>

        <DialogFooter>
          <Button onPress={handleSend} disabled={!recipientEmail.trim() || sendMutation.isPending}>
            <ButtonLabel>
              {sendMutation.isPending ? 'Lähetetään...' : 'Lähetä testiviesti'}
            </ButtonLabel>
          </Button>
          <DialogClose asChild>
            <Button variant="outline">
              <ButtonLabel>Peruuta</ButtonLabel>
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default function TemplateTranslationsPage() {
  const [templateType, setTemplateType] = useState<TemplateType>('email')
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(EMAIL_TEMPLATES[0].id)
  const [selectedVariant, setSelectedVariant] = useState<string | undefined>(
    EMAIL_TEMPLATES[0].defaultVariant ?? EMAIL_TEMPLATES[0].variants?.[0]?.id
  )
  const currentLanguage = useCurrentLanguage()

  const templates = templateType === 'email' ? EMAIL_TEMPLATES : SMS_TEMPLATES

  const selectedTemplate =
    templates.find((template) => template.id === selectedTemplateId) ?? templates[0]

  const handleTypeChange = (type: TemplateType) => {
    setTemplateType(type)
    const newTemplates = type === 'email' ? EMAIL_TEMPLATES : SMS_TEMPLATES
    setSelectedTemplateId(newTemplates[0].id)
  }

  useEffect(() => {
    setSelectedVariant(selectedTemplate.defaultVariant ?? selectedTemplate.variants?.[0]?.id)
  }, [selectedTemplate.defaultVariant, selectedTemplate.id, selectedTemplate.variants])

  const previewInput =
    templateType === 'email'
      ? {
          templateType: 'email' as const,
          templateId: selectedTemplate.id as AdminEmailTemplateId,
          variant: selectedVariant,
          language: currentLanguage,
        }
      : {
          templateType: 'sms' as const,
          templateId: selectedTemplate.id as AdminSmsTemplateId,
          variant: selectedVariant,
          language: currentLanguage,
        }

  const { data: preview, isLoading: isLoadingPreview } = useQuery(
    trpc.admin.getTemplatePreview.queryOptions(previewInput)
  )

  return (
    <ScreenView>
      <ScrollView>
        <ContentBox>
          <ViewTitle>Käännösten muokkaus</ViewTitle>
          <Body className="mt-2 text-secondary">
            Valitse viesti-template ja muokkaa käännöksiä suoraan Tolgee-editorilla klikkaamalla
            tekstejä.
          </Body>

          <View className="mt-6 gap-6">
            <View className="gap-3">
              <Label className="text-secondary">Viestin tyyppi</Label>
              <View className="flex-row gap-2">
                <Button
                  variant={templateType === 'email' ? 'default' : 'outline'}
                  onPress={() => handleTypeChange('email')}>
                  <ButtonLabel>Sähköposti</ButtonLabel>
                </Button>
                <Button
                  variant={templateType === 'sms' ? 'default' : 'outline'}
                  onPress={() => handleTypeChange('sms')}>
                  <ButtonLabel>SMS</ButtonLabel>
                </Button>
              </View>
            </View>

            <View className="gap-3">
              <Label className="text-secondary">Template</Label>
              <Select
                value={{ value: selectedTemplateId, label: selectedTemplate.name }}
                onValueChange={(option) => {
                  if (option) {
                    setSelectedTemplateId(option.value)
                  }
                }}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Valitse template" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {templates.map((template) => (
                      <SelectItem key={template.id} value={template.id} label={template.name}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </View>

            <View className="gap-3">
              <View className="flex-row items-center justify-between">
                <Heading3>Esikatselu</Heading3>
                {templateType === 'email' && <SendTestEmailDialog template={selectedTemplate} />}
              </View>
              {selectedTemplate.variants && selectedTemplate.variants.length > 0 ? (
                <View className="gap-2">
                  <Label className="text-secondary">Variantti</Label>
                  <View className="flex-row flex-wrap gap-2">
                    {selectedTemplate.variants.map((variant) => (
                      <Button
                        key={variant.id}
                        variant={selectedVariant === variant.id ? 'default' : 'outline'}
                        size="sm"
                        onPress={() => setSelectedVariant(variant.id)}>
                        <ButtonLabel>{variant.label}</ButtonLabel>
                      </Button>
                    ))}
                  </View>
                </View>
              ) : null}
              {isLoadingPreview || !preview ? (
                <LoadingView className="min-h-[240px]" />
              ) : (
                <MessagePreviewPanel preview={preview} />
              )}
            </View>

            <View className="gap-3">
              <Heading3>Käännösavaimet</Heading3>
              <View className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
                <Body className="mb-2 text-sm text-secondary">
                  Namespace: {selectedTemplate.namespace}
                </Body>
                {selectedTemplate.keys.map((key) => (
                  <Body key={key} className="font-mono text-xs text-neutral-600">
                    {key}
                  </Body>
                ))}
              </View>
            </View>
          </View>
        </ContentBox>
      </ScrollView>
    </ScreenView>
  )
}
